import React, { useEffect, useState } from "react";
import api from "../utils/axios";
import Swal from "sweetalert2";

interface KycRequest {
  id: string | number;
  name: string;
  email: string;
  phone: string;
  kyc_status: string;
  kyc_document_type: string;
  kyc_document_path: string;
  kyc_submitted_at: string;
  kyc_rejection_reason: string | null;
}

const KycManagement: React.FC = () => {
  const [kycRequests, setKycRequests] = useState<KycRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // empty means all kyc requests
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 450);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchKycRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get("/kyc-list", {
        params: {
          page,
          limit,
          status: statusFilter || undefined,
          search: debouncedSearch || undefined,
        },
      });
      if (res.data?.status) {
        setKycRequests(res.data.data || []);
        setTotalCount(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching KYC requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKycRequests();
  }, [debouncedSearch, statusFilter, page]);

  const handleApprove = async (request: KycRequest) => {
    const result = await Swal.fire({
      title: "Approve KYC?",
      text: `Are you sure you want to verify the identity of ${request.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, approve",
      confirmButtonColor: "#22c55e",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await api.post("/kyc-action", {
        uid: request.id,
        action: "approve",
      });

      if (res.data?.status) {
        Swal.fire({
          icon: "success",
          title: "Approved!",
          text: `KYC for ${request.name} has been approved.`,
        });
        fetchKycRequests();
      }
    } catch (error: any) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.msg || "Failed to approve KYC",
      });
    }
  };

  const handleReject = async (request: KycRequest) => {
    const result = await Swal.fire({
      title: "Reject KYC Request",
      input: "textarea",
      inputLabel: `Specify reason for rejecting ${request.name}'s KYC`,
      inputPlaceholder: "Enter rejection reason...",
      inputAttributes: {
        maxlength: "255",
      },
      showCancelButton: true,
      confirmButtonText: "Reject",
      confirmButtonColor: "#ef4444",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value) {
          return "Rejection reason is required!";
        }
      },
    });

    if (!result.isConfirmed) return;

    try {
      const res = await api.post("/kyc-action", {
        uid: request.id,
        action: "reject",
        rejection_reason: result.value,
      });

      if (res.data?.status) {
        Swal.fire({
          icon: "success",
          title: "Rejected!",
          text: `KYC for ${request.name} has been rejected.`,
        });
        fetchKycRequests();
      }
    } catch (error: any) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.msg || "Failed to reject KYC",
      });
    }
  };

  return (
    <div className="">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">KYC Verification</h1>
          <p className="text-sm text-slate-500 mt-1">
            Review and manage manual identity verification requests
          </p>
        </div>

        {/* Tab Filters */}
        <div 
          className="flex bg-slate-100 p-1.5 rounded-xl border border-slate-200 overflow-x-auto max-w-full whitespace-nowrap"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {[
            { label: "All Requests", value: "" },
            { label: "Pending", value: "pending" },
            { label: "Approved", value: "approved" },
            { label: "Rejected", value: "rejected" },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setStatusFilter(tab.value);
                setPage(1);
              }}
              className={`px-4 py-2 text-xs font-semibold rounded-lg border-none transition-all cursor-pointer flex-shrink-0 whitespace-nowrap ${statusFilter === tab.value
                ? "bg-white text-slate-800 shadow"
                : "bg-transparent text-slate-500 hover:text-slate-800"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search Filter */}
      <div className="mb-6 flex gap-4 max-w-md">
        <input
          type="text"
          placeholder="Search by ID, Username, Email, Phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border rounded-xl outline-none focus:border-orange-500 text-sm"
        />
      </div>

      {/* KYC Table Grid */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                <th className="px-6 py-4">User ID</th>
                <th className="px-6 py-4">User Details</th>
                <th className="px-6 py-4">Doc Type</th>
                <th className="px-6 py-4">Doc Attachment</th>
                <th className="px-6 py-4">Submitted At</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500 font-medium">
                    Loading requests...
                  </td>
                </tr>
              ) : kycRequests.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500 font-medium">
                    No KYC verification requests found.
                  </td>
                </tr>
              ) : (
                kycRequests.map((req) => (
                  <tr key={req.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-6 py-4 font-mono font-medium text-slate-700">
                      {req.id}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-800 text-sm">{req.name}</span>
                        <span className="text-xs text-slate-500 mt-0.5">{req.email}</span>
                        <span className="text-xs text-slate-500 mt-0.5">{req.phone}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded text-xs font-semibold">
                        {req.kyc_document_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {req.kyc_document_path ? (
                        <a
                          href={req.kyc_document_path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-orange-600 hover:text-orange-700 font-bold hover:underline"
                        >
                          View Document
                        </a>
                      ) : (
                        <span className="text-slate-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-600 text-xs">
                      {new Date(req.kyc_submitted_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start gap-1">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-bold ${req.kyc_status === "pending"
                            ? "bg-amber-100 text-amber-800"
                            : req.kyc_status === "approved"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-rose-100 text-rose-800"
                            }`}
                        >
                          {req.kyc_status.toUpperCase()}
                        </span>
                        {req.kyc_status === "rejected" && req.kyc_rejection_reason && (
                          <span className="text-[10px] text-slate-500 leading-normal max-w-xs break-words">
                            <strong>Reason:</strong> {req.kyc_rejection_reason}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      {req.kyc_status === "pending" ? (
                        <>
                          <button
                            onClick={() => handleApprove(req)}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-3.5 py-1.5 rounded-lg border-none cursor-pointer transition shadow hover:shadow-md"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(req)}
                            className="bg-rose-500 hover:bg-rose-600 text-white font-bold text-xs px-3.5 py-1.5 rounded-lg border-none cursor-pointer transition shadow hover:shadow-md"
                          >
                            Reject
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400 font-medium">No actions</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {!loading && kycRequests.length > 0 && (
          <div className="flex justify-between items-center px-6 py-4 border-t border-slate-200">
            <span className="text-xs text-slate-500">
              Showing {kycRequests.length} of {totalCount} records
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 border border-slate-200 text-xs font-semibold rounded-lg disabled:opacity-40 cursor-pointer hover:bg-slate-50"
              >
                Previous
              </button>
              <span className="text-xs font-semibold text-slate-700 px-2">
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 border border-slate-200 text-xs font-semibold rounded-lg disabled:opacity-40 cursor-pointer hover:bg-slate-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KycManagement;
